import { useMemo, useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import DOMPurify from "dompurify";
import { ArrowLeft, Clock, Calendar, ChevronRight, BookOpen } from "lucide-react";
import { getBlogBySlug } from "@/data/blogs";
import blogPosts from "@/data/blogs";

const API = process.env.REACT_APP_BACKEND_URL;

const categoryColors = {
  Banking: "bg-vedanti-navy text-white",
  SSC: "bg-vedanti-orange text-white",
  Railway: "bg-green-600 text-white",
  MBA: "bg-vedanti-mango text-vedanti-navy",
  Insurance: "bg-blue-600 text-white",
  Career: "bg-purple-600 text-white",
  Vedanti: "bg-gradient-to-r from-vedanti-orange to-vedanti-mango text-white",
};

export default function BlogPostPage() {
  const { slug } = useParams();
  const staticPost = getBlogBySlug(slug);
  const [post, setPost] = useState(staticPost || null);
  const [status, setStatus] = useState(staticPost ? "ok" : "loading");

  useEffect(() => {
    if (staticPost) {
      setPost(staticPost);
      setStatus("ok");
      return;
    }
    let active = true;
    setStatus("loading");
    fetch(`${API}/api/blogs/${slug}`)
      .then((r) => { if (!r.ok) throw new Error("not found"); return r.json(); })
      .then((d) => { if (active) { setPost({ ...d, readTime: d.read_time }); setStatus("ok"); } })
      .catch(() => { if (active) setStatus("error"); });
    return () => { active = false; };
  }, [slug, staticPost]);

  // Sanitize blog HTML to prevent XSS. Computed before any early return so the hook
  // call order remains stable across renders (React rules of hooks).
  const sanitizedContent = useMemo(
    () => (post ? DOMPurify.sanitize(post.content, { USE_PROFILES: { html: true } }) : ""),
    [post]
  );

  if (status === "loading") {
    return <div className="min-h-[60vh] flex items-center justify-center"><div className="h-10 w-10 border-4 border-vedanti-orange border-t-transparent rounded-full animate-spin" /></div>;
  }

  if (status === "error" || !post) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
        <h1 className="text-2xl font-bold text-vedanti-navy">Article Not Found</h1>
        <Link to="/resources" className="text-vedanti-orange font-semibold hover:underline flex items-center gap-1"><ArrowLeft size={16} /> Back to Resources</Link>
      </div>
    );
  }

  const related = blogPosts.filter(b => b.category === post.category && b.slug !== post.slug).slice(0, 3);

  return (
    <div data-testid="blog-post-page">
      <Helmet>
        <title>{`${post.title} | Vedanti Academy Resources`}</title>
        <meta name="description" content={post.excerpt} />
        <link rel="canonical" href={`https://vedantiacademy.com/resources/${post.slug}`} />
      </Helmet>

      {/* Breadcrumb */}
      <div className="bg-slate-50 border-b border-slate-100">
        <div className="container-main py-3 flex items-center gap-2 text-sm text-slate-500">
          <Link to="/" className="hover:text-vedanti-orange transition-colors">Home</Link>
          <ChevronRight size={14} />
          <Link to="/resources" className="hover:text-vedanti-orange transition-colors">Resources</Link>
          <ChevronRight size={14} />
          <span className="text-vedanti-navy font-medium truncate max-w-[200px]">{post.title}</span>
        </div>
      </div>

      <article className="section-padding bg-white">
        <div className="container-main max-w-4xl">
          {/* Header */}
          <div className="mb-8">
            <span className={`inline-block text-xs font-bold px-3 py-1.5 rounded-full mb-4 ${categoryColors[post.category] || "bg-slate-200 text-slate-700"}`}>
              {post.category}
            </span>
            <h1 className="font-heading text-2xl md:text-4xl font-extrabold text-vedanti-navy leading-tight mb-4" data-testid="blog-title">
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
              <span className="flex items-center gap-1.5"><Calendar size={14} /> {post.date}</span>
              <span className="flex items-center gap-1.5"><Clock size={14} /> {post.readTime} read</span>
              <span className="flex items-center gap-1.5"><BookOpen size={14} /> By {post.author}</span>
            </div>
          </div>

          {/* Content */}
          <div
            className="prose prose-slate prose-lg max-w-none prose-headings:font-heading prose-headings:text-vedanti-navy prose-h2:text-xl prose-h2:mt-8 prose-h2:mb-3 prose-p:text-slate-600 prose-p:leading-relaxed prose-li:text-slate-600 prose-strong:text-vedanti-navy prose-a:text-vedanti-orange"
            dangerouslySetInnerHTML={{ __html: sanitizedContent }}
            data-testid="blog-content"
          />

          {/* CTA */}
          <div className="mt-12 bg-gradient-to-r from-vedanti-navy to-blue-900 rounded-2xl p-8 text-center">
            <h3 className="font-heading text-xl font-bold text-white mb-2">Ready to Start Your Preparation?</h3>
            <p className="text-slate-300 text-sm mb-5">Join Vedanti Academy and get expert guidance, study materials, and mock tests.</p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/demo-class" className="bg-vedanti-orange hover:bg-orange-600 text-white font-bold px-6 py-3 rounded-xl transition-colors" data-testid="blog-cta-demo">Book Free Demo</Link>
              <Link to="/consultancy" className="border border-white/25 text-white hover:bg-white/10 font-semibold px-6 py-3 rounded-xl transition-colors" data-testid="blog-cta-consult">Get Counselling</Link>
            </div>
          </div>

          {/* Back */}
          <Link to="/resources" className="inline-flex items-center gap-2 text-vedanti-orange font-semibold mt-8 hover:gap-3 transition-all" data-testid="back-to-resources">
            <ArrowLeft size={16} /> Back to All Resources
          </Link>
        </div>
      </article>

      {/* Related Posts */}
      {related.length > 0 && (
        <section className="section-padding bg-slate-50">
          <div className="container-main max-w-4xl">
            <h2 className="font-heading text-xl font-bold text-vedanti-navy mb-6">Related Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {related.map((r) => (
                <Link to={`/resources/${r.slug}`} key={r.id} className="bg-white rounded-xl p-4 border border-slate-100 hover:border-vedanti-orange/30 hover:shadow-lg transition-all group" data-testid={`related-${r.id}`}>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${categoryColors[r.category]}`}>{r.category}</span>
                  <h3 className="font-bold text-vedanti-navy text-sm mt-2 leading-snug group-hover:text-vedanti-orange transition-colors line-clamp-2">{r.title}</h3>
                  <p className="text-xs text-slate-400 mt-2">{r.readTime} read</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
